import { fetchApi, trackError } from '@vend/utilities'

interface CreditAccountBalances {
  pendingPaymentAmount?: string
}

interface CreditAccountResponse {
  balances?: CreditAccountBalances
}

export type PendingPaymentAmountResult =
  | { known: true; amount: number }
  | { known: false }

export const buildCreditAccountUrl = (customerId: string): string =>
  `/api/2.0/invoicing/v1/credit-accounts/x-series_${encodeURIComponent(
    customerId
  )}`

const CREDIT_ACCOUNT_FETCH_ERROR =
  '[AccountBalanceSetup] Failed to fetch pending payment amount'

const parsePendingPaymentAmount = (data: CreditAccountResponse): number => {
  const amount = parseFloat(data?.balances?.pendingPaymentAmount ?? '')
  return Number.isFinite(amount) && amount > 0 ? amount : 0
}

/**
 * Account-level ACH in-transit amount from invoicing.
 * 404 / unprovisioned account is expected (lazy provisioning) and returns 0
 * without logging (`known: true`).
 * 5xx / network failures return `known: false` so the register modal can
 * fail-closed (QM-9059) instead of allowing a full-balance overpay.
 */
export const fetchPendingPaymentAmount = async (
  customerId: string
): Promise<PendingPaymentAmountResult> => {
  const url = buildCreditAccountUrl(customerId)

  try {
    const response = await fetchApi(url)

    if (response.status === 404) {
      return { known: true, amount: 0 }
    }

    if (!response.ok) {
      trackError(CREDIT_ACCOUNT_FETCH_ERROR, {
        customerId,
        status: response.status,
      })
      return { known: false }
    }

    const data: CreditAccountResponse = await response.json()
    return { known: true, amount: parsePendingPaymentAmount(data) }
  } catch (error) {
    trackError(CREDIT_ACCOUNT_FETCH_ERROR, { customerId, error })
    return { known: false }
  }
}
